import {
  Stack,
  TextInput,
  Textarea,
  Flex,
  Text,
  Divider,
  Checkbox,
  Button,
} from "@mantine/core"
import { Suspense } from "react"
import { CancelButton, SubmitButton } from "../../../-components/Buttons"
import { DefaultList } from "../../../-components/default-list"
import { DefaultListItem } from "../../../-components/default-list/list-item"
import { FormButtonGroup } from "../../../-components/form-button-group"
import { UserAvatar } from "../../../-components/UserAvatar"
import { PersonNameWithUserNameFallback } from "./ChatListForCurrentUser/PersonNameWithUserNameFallback"
import { UseFormReturnType } from "@mantine/form"
import { ChatType } from "../../../orval/gen/model"
import { useTranslation } from "react-i18next"
import { useGetAuthenticatedUser } from "../../app/-components/use-authenticated-user"
import { DottedMenu } from "../../../-components/dotted-menu"
import { IconTrash } from "@tabler/icons-react"
import { useDefaultInfiniteQuery } from "@/utils/useDefaultInfiniteQuery"
import { useDebouncedValue } from "@mantine/hooks"
import {
  getGetPaginatedSimpleUsersWithPersonNameQueryKey,
  getPaginatedSimpleUsersWithPersonName,
} from "@/orval/gen"
import { CreateChatMember, UserTypeFilter } from "@/orval/gen/model"
import { SingleSelectFromInfiniteQuery } from "@components/SelectFromQuery/SingleSelectFromInfiniteQuery"

interface FormValues {
  name?: string | null
  description?: string | null
  chatMembers: Array<CreateChatMember>
  chatType: ChatType
  isReadOnly: boolean
}

interface Props {
  form: UseFormReturnType<FormValues, (values: FormValues) => FormValues>
  onSubmit: (values: FormValues) => void
  onCancel: () => void
  readonly?: boolean
}

export function CreateUpdateChatForm({
  form,
  onSubmit,
  onCancel,
  readonly = false,
}: Props) {
  const chatType = form.getValues().chatType
  const currentUserId = useGetAuthenticatedUser().data.userId
  const adminOnlyType = ["Broadcast", "Feed", "BroadcastFeed"].includes(
    chatType
  )
  const handleSelectUser = (userId: number) =>
    !form.getValues().chatMembers.some((cm) => cm.userId === userId) &&
    chatType === "Direct"
      ? form.setFieldValue("chatMembers", [
          ...form
            .getValues()
            .chatMembers.filter((cm) => cm.userId === currentUserId),
          { userId: userId, isAdmin: false },
        ])
      : form.insertListItem("chatMembers", {
          userId: userId,
          isAdmin: adminOnlyType,
        })

  const { t } = useTranslation("app_server")
  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <Stack>
        {chatType !== "Direct" && (
          <>
            <TextInput
              disabled={readonly}
              required
              label={t("Name")}
              key={form.key("name")}
              {...form.getInputProps("name")}
            />
            <Textarea
              disabled={readonly}
              label={t("Description")}
              key={form.key("description")}
              {...form.getInputProps("description")}
            />
          </>
        )}
        {(chatType === "PrivateGroup" || chatType === "PublicGroup") && (
          <Checkbox
            disabled={readonly}
            label={t("PublicGroup")}
            checked={chatType === "PublicGroup"}
            onChange={() =>
              form.setFieldValue(
                "chatType",
                chatType === "PublicGroup" ? "PrivateGroup" : "PublicGroup"
              )
            }
          />
        )}
        {chatType !== "Direct" && (
          <>
            <Checkbox
              label={t("ReadOnly")}
              description={t("ReadOnlyDescription")}
              disabled={readonly}
              key={form.key("isReadOnly")}
              {...form.getInputProps("isReadOnly", { type: "checkbox" })}
            />
            <Divider />
            <Text>{adminOnlyType ? t("Admins") : t("Members")}</Text>
          </>
        )}
        {!readonly && (
          <UserPicker
            excludeIds={[
              currentUserId,
              ...form.getValues().chatMembers.map((cm) => cm.userId),
            ]}
            onSelect={handleSelectUser}
          />
        )}
        <DefaultList
          flex={1}
          key={form.key("chatMembers")}
          data={form.getValues().chatMembers}
          itemRenderCondition={(entity) =>
            chatType === "Feed"
              ? entity.isAdmin
              : chatType !== "Direct" || entity.userId !== currentUserId
          }
          renderItem={({ entity, index }) => (
            <Suspense>
              <DefaultListItem>
                <UserAvatar userId={entity.userId} />
                <PersonNameWithUserNameFallback userId={entity.userId} />
                {entity.userId === currentUserId && ` (${t("You")})`}
                {entity.isAdmin && " (Admin)"}
                <Flex flex="1" />
                {!readonly &&
                  chatType !== "Direct" &&
                  entity.userId !== currentUserId && (
                    <DottedMenu
                      items={[
                        ...(!adminOnlyType
                          ? [
                              {
                                children: entity.isAdmin
                                  ? t("RemoveAdmin")
                                  : t("MakeAdmin"),
                                onClick: () =>
                                  form.replaceListItem("chatMembers", index, {
                                    userId: entity.userId,
                                    isAdmin: !entity.isAdmin,
                                  }),
                              },
                            ]
                          : []),
                        {
                          children: t("Remove"),
                          rightSection: <IconTrash />,
                          onClick: () =>
                            form.removeListItem("chatMembers", index),
                        },
                      ]}
                    />
                  )}
              </DefaultListItem>
            </Suspense>
          )}
        />
        <FormButtonGroup>
          {readonly ? (
            <Button onClick={onCancel}>{t("Close")}</Button>
          ) : (
            <>
              <CancelButton onClick={onCancel} />
              <SubmitButton>
                {Object.keys(form.getValues()).includes("chatId")
                  ? t("Update")
                  : t("Create")}
              </SubmitButton>
            </>
          )}
        </FormButtonGroup>
      </Stack>
    </form>
  )
}

interface UserPickerProps {
  excludeIds: number[]
  onSelect: (userId: number) => void
}
function UserPicker({ excludeIds: excludeUserIds, onSelect }: UserPickerProps) {
  const [search, setSearch] = React.useState("")
  const [debouncedSearch] = useDebouncedValue(search, 500)

  const queryOptions = {
    userTypeFilter: UserTypeFilter.Users,
    excludeUserIds: excludeUserIds,
    searchString: debouncedSearch,
    filterInactive: true,
    includeUserIds: [],
  }

  const queryKey = getGetPaginatedSimpleUsersWithPersonNameQueryKey({
    ...queryOptions,
  })

  const queryResult = useDefaultInfiniteQuery(
    getPaginatedSimpleUsersWithPersonName,
    (pageRequest) => ({
      ...pageRequest,
      ...queryOptions,
    }),
    {
      pageSize: 25,
      queryKey: queryKey,
    }
  )

  const { t } = useTranslation("app_server")
  return (
    <SingleSelectFromInfiniteQuery
      queryData={queryResult}
      entitiesToComboboxData={(data) => ({
        label: data.userName,
        value: data.userId.toString(),
      })}
      searchable
      placeholder={t("Users")}
      searchValue={search}
      onSearchChange={(v) => setSearch(v)}
      onSelectionChange={(v) => v && onSelect(parseInt(v))}
    />
  )
}
